import Backbone from 'backbone'
import _ from 'underscore'
import HomeView from './views/home'
import MenuView from './views/menu'
import ContentView from './views/content'
import ProfileView from './views/profile'
import SchoolView from './views/school'
import TestResultView from './views/test-results'
import user from './user'
import RegisterFreeBookView from './views/register-free-book'
import RegisterView from './views/register'
import PromoCodeView from './views/promo-code'
import titles from './titles'
import LoginView from './views/login'
import JoinSchoolView from './views/join-school'
import CreateSchoolView from './views/create-school'
import ForgottenPasswordView from './views/forgotten-password'
import config from './config'
import { loadView } from './overrides'
import Alert from './helpers/alert'
import Request from './helpers/request'
import UserModel from './models/user'

let currentView = null

// Helper: redirect in a clean way using replace:true to avoid cluttering history
const redirectTo = (route) => {
    Backbone.history.navigate(route, { trigger: true, replace: true })
}

// Helper: find the title by image_prefix. If not found, redirect to the home route.
const findTitleOrRedirect = (slug, image_prefix) => {
    const title = titles.findWhere({ image_prefix })
    if (!title) {
        redirectTo(`app/${slug}`)
        return null
    }
    return title
}

// Helper: Validate that the app for the given slug exists.
// If not, alert the user and redirect to the menu.
const loadAppIfNeeded = async (slug) => {
    const appExists = config.get('apps').find(app => app.slug === slug)

    if (!appExists) {
        Alert('App does not exist')
        redirectTo('')
        return false
    }

    if (slug !== config.get('slug')) {
        await config.loadApp(slug)
    }

    return true
}

const switchView = async (view, slug) => {
    if (currentView) {
        currentView.stopListening()
        currentView.undelegateEvents()
        currentView.off()
        currentView.$el.empty()
    }

    if (slug && slug !== config.get('slug')) {
        const loaded = await loadAppIfNeeded(slug)
        if (!loaded) return
    }

    currentView = view
    currentView.render()
}

const requireAuth = (routeHandler) => function() {
    const authenticated = user.get('uid') !== null

    if (!authenticated) {
        config.set('returnRoute', Backbone.history.getFragment())
        redirectTo('login')
    } else {
        routeHandler.apply(this, arguments)
    }
}

const requirePurchase = (routeHandler) => async function(slug, image_prefix) {
    const loaded = await loadAppIfNeeded(slug)
    if (!loaded) return

    // Look up title to validate image_prefix
    const title = titles.findWhere({ image_prefix })
    if (!title) {
        redirectTo(`app/${slug}`)
        return
    }

    const purchased = user.get('purchased').purchased()
    const free = title.get('free')

    if (!purchased && !free) {
        config.set('returnRoute', Backbone.history.getFragment())
        redirectTo('free-book')
    } else {
        routeHandler.apply(this, arguments)
    }
}

// Helper function to select the reporting user model based on classId and userId.
const selectReportingUser = async (slug, classId, userId) => {
    if (classId && userId) {
        try {
            const response = await Request({
                url: `/api/school/class/${classId}/student`,
                method: 'GET',
                data: {
                    sort: 'firstnameLowercase lastnameLowercase',
                    limit: 500,
                },
            });
            // Find the student whose _id matches the provided userId.
            const userItem = _.find(response.students, student => student._id === userId);
            if (!userItem) {
                redirectTo(`app/${slug}`);
                return null;
            }
            return new UserModel({
                id: userItem._id,
                firstname: userItem.firstname,
                lastname: userItem.lastname,
            });
        } catch (error) {
            redirectTo(`app/${slug}`);
            return null;
        }
    } else {
        // Default to the logged in user if no userId is provided.
        return user;
    }
};

export default Backbone.Router.extend({
    routes: {
        '': function() {
            config.unloadApp()
            return switchView(new MenuView())
        },
        'login': () => {
            config.unloadApp()
            return switchView(new LoginView())
        },
        'register': () => {
            config.unloadApp()
            return switchView(new RegisterView())
        },
        'free-book': () => {
            config.unloadApp()
            if (user.get('uid')) {
                redirectTo('promo-code')
                return
            }
            return switchView(new RegisterFreeBookView({ model: user }))
        },
        'promo-code': requireAuth(() => {
            config.unloadApp()
            return switchView(new PromoCodeView({ model: user }))
        }),
        'profile': requireAuth(() => {
            config.unloadApp()
            return switchView(new ProfileView({ model: user }))
        }),
        'school': requireAuth(() => {
            config.unloadApp()
            return switchView(new SchoolView({ model: user }))
        }),
        'join-school': requireAuth(() => {
            config.unloadApp()
            return switchView(new JoinSchoolView({ model: user }))
        }),
        'create-school': requireAuth(() => {
            config.unloadApp()
            return switchView(new CreateSchoolView({ model: user }))
        }),
        'forgotten-password': () => switchView(new ForgottenPasswordView()),
        'app/:slug': async (slug) => {
            const loaded = await loadAppIfNeeded(slug)
            if (!loaded) return
            return switchView(new HomeView(), slug)
        },
        'app/:slug/reporting(/:classId)(/:userId)': async (slug, classId, userId) => {
            const loaded = await loadAppIfNeeded(slug);
            if (!loaded) return;

            const ReportingView = loadView('reporting');

            const selectedUser = await selectReportingUser(slug, classId, userId);
            if (!selectedUser) return;

            return switchView(new ReportingView({ model: selectedUser }), slug);
        },
        'app/:slug/content/:image_prefix': requirePurchase((slug, image_prefix) => {
            const title = findTitleOrRedirect(slug, image_prefix)
            if (!title) {
                redirectTo(`app/${slug}`)
                return
            }
            return switchView(new ContentView({ model: title }), slug)
        }),
        'app/:slug/test/:image_prefix': requirePurchase((slug, image_prefix) => {
            const title = findTitleOrRedirect(slug, image_prefix)
            if (!title) {
                redirectTo(`app/${slug}`)
                return
            }

            if (title.get('test_available') === false) {
                // Redirect to content if test is not available
                redirectTo(`app/${slug}/content/${image_prefix}`)
                return
            }
            const TestView = loadView('test', image_prefix)
            return switchView(new TestView({ model: title }), slug)
        }),
        'app/:slug/test-result/:image_prefix': requirePurchase((slug, image_prefix) => {
            const title = findTitleOrRedirect(slug, image_prefix)
            if (!title) {
                redirectTo(`app/${slug}`)
                return
            }

            if (title.get('test').get('end_timestamp') === null) {
                // Redirect to test if the test has not finished
                redirectTo(`app/${slug}/test/${image_prefix}`)
                return
            }
            return switchView(new TestResultView({ image_prefix }), slug)
        }),
        'app/:slug/promo-code': requireAuth((slug) => {
            return switchView(new PromoCodeView({ model: user }), slug)
        }),
        '*path': function() {
            redirectTo('');
        }
    }
})
